import { Component, DoCheck, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import moment from 'moment';
import { Moment } from 'moment';
import { TsDatepickerComponent } from 'tsui';
import { FormFieldComponent } from '../form-field.types';

const DATE_FORMAT_ERROR_MESSAGE = 'Date should be in MM/DD/YYYY format.';
const MIN_FULL_YEAR = 1000;

@Component({
  templateUrl: './date-form-field.component.html',
  styleUrls: ['./../form-field.component.scss'],
})
export class DateFormFieldComponent extends FormFieldComponent implements OnInit, DoCheck {
  @ViewChild(TsDatepickerComponent) private readonly datepicker?: TsDatepickerComponent;

  private _originalValue: string;

  public dateDisplayFormat = 'MM/DD/YYYY';
  public editing = false;
  public date = new UntypedFormControl(null);
  // Overrides the inherited Material-specific selector: ts-datepicker has no .mat-mdc-text-field-wrapper to target.
  public readonly commonTargetStylingClass = '.ts-datepicker';
  // Only `incorrect` is mapped so mid-typing parse errors stay silent until a date is committed.
  public readonly dateErrorMessages = { incorrect: DATE_FORMAT_ERROR_MESSAGE };

  private syncDisabledState(): void {
    // [SC-57579] With `matDatepickerInput` + reactive forms, the FormControl's disabled state is authoritative.
    // Keep it in sync with the computed `isDisabled` (locked/readonly/archived/value_hidden).
    if (this.isDisabled) {
      if (!this.date.disabled) this.date.disable({ emitEvent: false });
    } else {
      if (this.date.disabled) this.date.enable({ emitEvent: false });
    }
  }

  // Duck-typed contract read by form-field.component.ts to avoid clobbering an in-progress edit.
  get inputControl(): ElementRef<HTMLInputElement> | undefined {
    return this.datepicker?.inputElement();
  }

  get modelFieldValue(): string {
    return this.model[this.fieldDef.name];
  }

  get modelFieldMomentValue(): Moment {
    return moment(this.modelFieldValue || '');
  }

  get modelFieldFormattedValue(): string {
    return this.modelFieldMomentValue?.format(this.dateDisplayFormat);
  }

  get dateFieldFormattedValue(): string {
    return this.date.value?.format(this.dateDisplayFormat);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this._originalValue = this.modelFieldFormattedValue;

    const modelDate = this.modelFieldMomentValue;
    this.date.setValue(this.dateOrNull(modelDate));

    // Written back as the parsed moment (invalid when empty), as before the migration: callers test the model for truthiness.
    this.model[this.fieldDef.name] = modelDate;

    this.syncDisabledState();
  }

  ngDoCheck(): void {
    this.syncDisabledState();
  }

  onDateChange(): void {
    if (this._originalValue !== this.dateFieldFormattedValue) {
      const dbFormattedDate = this.hasFullYear() ? this.date.value.format('YYYY-MM-DD') : null;
      if (dbFormattedDate) this.save(dbFormattedDate);
      else this.date.setErrors({ incorrect: true });
    }
  }

  dateBlur(): void {
    if (this.inputControl?.nativeElement.value === '') {
      this.date.setValue(null);
      if (this._originalValue) this.save(this.date.value);
    }
  }

  save(date: string): void {
    this.model[this.fieldDef.name] = date;
    this._originalValue = this.dateFieldFormattedValue;
    this.update.emit({ [this.fieldDef.name]: date });
  }

  updateChangedModel?(model: any): void {
    this.model = model;
    this.date.setValue(this.dateOrNull(this.modelFieldMomentValue));
    this.syncDisabledState();
  }

  // An empty model value parses to an invalid moment, which the datepicker would flag as a parse error.
  private dateOrNull(date: Moment): Moment | null {
    return date.isValid() ? date : null;
  }

  // The date mask drops pasted slashes, so '12/1/2022' becomes '12/12/022' and would parse as the year 22.
  private hasFullYear(): boolean {
    return this.date.value?.year() >= MIN_FULL_YEAR;
  }
}
